// src/components/Auth/SignUp.js
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth, googleAuthProvider, db } from "../../firebase"; 
import { createUserWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore"; 
import "../../styles/AuthStyles.css";

// Utility icons
const BackArrowIcon = () => (
    <svg className="back-arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5M12 19l-7-7 7-7"></path>
    </svg>
);
const GoogleIcon = () => (
    <svg viewBox="0 0 24 24" className="google-icon" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.24 10.29v3.08h6.14c-.25 1.57-1.14 2.82-2.58 3.65l3.22 2.5a10.02 10.02 0 0 0 3.78-8.91c0-4.9-3.99-8.9-8.9-8.9s-8.9 3.99-8.9 8.9a8.88 8.88 0 0 0 2.58 6.27l3.22-2.5a5.27 5.27 0 0 1-2.02-3.77z" fill="#4285F4"/>
        <path d="M5.44 14.86a5.3 5.3 0 0 1 0-5.72l-3.22-2.5a8.91 8.91 0 0 0 0 10.72l3.22-2.5z" fill="#34A853"/>
        <path d="M12.24 5.33c1.62 0 3.09.56 4.24 1.51l2.87-2.87c-1.89-1.78-4.32-2.77-7.11-2.77a8.9 8.9 0 0 0-6.31 2.61l3.22 2.5c1.15-.95 2.62-1.51 4.24-1.51z" fill="#FBBC05"/>
        <path d="M12.24 21.67c-3.1 0-5.91-1.3-7.91-3.3l-3.22 2.5a10.9 10.9 0 0 0 11.13 0l-3.22-2.5c-1.39 1-3.08 1.57-4.8 1.57z" fill="#EA4335"/>
    </svg>
);

const EyeIconOpen = ({ onClick, className }) => (
    <svg onClick={onClick} className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
        <circle cx="12" cy="12" r="3"></circle>
    </svg>
);

const EyeIconClosed = ({ onClick, className }) => (
    <svg onClick={onClick} className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-8-10-8s2.92-5.74 8-6m0 4a4 4 0 1 0 0-8M22 2L2 22"></path>
    </svg>
);


const Signup = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false); // NEW STATE
    const [showConfirmPassword, setShowConfirmPassword] = useState(false); // NEW STATE
    const navigate = useNavigate();

    // Toggle functions
    const togglePasswordVisibility = () => {
        setShowPassword(prev => !prev);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(prev => !prev);
    };

    // Function to determine the user role
    const determineRole = (email) => {
        if (email && email.includes("trendzz")) {
            return "client";
        }
        return "customer";
    };

    // Function to handle Email/Password Signup
    const handleSignUp = async (e) => {
        e.preventDefault();
        if (loading) return;

        setError("");
        setLoading(true);

        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            setLoading(false);
            return;
        }

        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            const role = determineRole(email);
            const userDocRef = doc(db, "users", user.uid);

            const userDocSnap = await getDoc(userDocRef);
            if (!userDocSnap.exists()) {
                await setDoc(userDocRef, {
                    email: user.email,
                    role: role,
                });
            }

            navigate(`/${role === "client" ? "client" : "customer"}/dashboard`, { replace: true });
        } catch (error) {
            console.error("Signup failed:", error.message);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Function to handle Google Sign Up
    const signUpWithGoogle = async () => {
        if (loading) return;
        
        setError("");
        setLoading(true);
        try {
            const userCredential = await signInWithPopup(auth, googleAuthProvider);
            const user = userCredential.user;

            const role = determineRole(user.email);
            const userDocRef = doc(db, "users", user.uid);

            const userDocSnap = await getDoc(userDocRef);
            if (!userDocSnap.exists()) {
                await setDoc(userDocRef, {
                    email: user.email,
                    role: role,
                });
            }

            navigate(`/${role === "client" ? "client" : "customer"}/dashboard`, { replace: true });
        } catch (error) {
            console.error("Google sign-up failed:", error.message);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-screen-container">
            <div className="auth-header-art">
                <button className="back-button" onClick={() => navigate(-1)} aria-label="Go Back">
                    <BackArrowIcon />
                </button>
                <div className="clean-logo-placeholder">
                    <span className="logo-text-large">
                        NUTRITION TRENDS
                    </span>
                    <p className="logo-subtext">Start Your Health Journey Today</p>
                </div>
            </div>

            <div className="auth-form-card">
                <h1 className="auth-title">Create Your Account</h1>

                {error && <p className="auth-error">{error}</p>}
                
                <form onSubmit={handleSignUp} className="auth-form">
                    <div className="input-group">
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email address"
                            required
                            disabled={loading}
                        />
                    </div>
                    <div className="input-group password-group">
                        <input
                            // Dynamic type based on state
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Password"
                            required
                            disabled={loading}
                        />
                        {/* Conditional Icon Rendering */}
                        {showPassword ? (
                            <EyeIconOpen onClick={togglePasswordVisibility} className="password-toggle-icon" />
                        ) : (
                            <EyeIconClosed onClick={togglePasswordVisibility} className="password-toggle-icon" />
                        )}
                    </div>
                    <div className="input-group password-group"> 
                        <input
                            // Dynamic type based on state
                            type={showConfirmPassword ? 'text' : 'password'}
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm Password"
                            required
                            disabled={loading}
                        />
                        {/* Conditional Icon Rendering */}
                        {showConfirmPassword ? (
                            <EyeIconOpen onClick={toggleConfirmPasswordVisibility} className="password-toggle-icon" />
                        ) : (
                            <EyeIconClosed onClick={toggleConfirmPasswordVisibility} className="password-toggle-icon" />
                        )}
                    </div>
                    
                    <button type="submit" className="primary-auth-button" disabled={loading}>
                        {loading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
                    </button>

                    <div className="or-divider">OR</div>
                    
                    <button 
                        type="button" 
                        onClick={signUpWithGoogle} 
                        disabled={loading}
                        className="google-auth-button"
                    >
                        <GoogleIcon />
                        {loading ? 'Signing Up...' : 'Sign Up with Google'}
                    </button>


                    <p className="terms-text">
                        By signing up, you agree to our <Link to="/terms" className="auth-link">Terms of Use</Link>
                    </p>

                    <p className="auth-footer-text">
                        Already have an account? <Link to="/login" className="auth-link">Log In</Link>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default Signup;